
import Button from 'components/base/Button';
import { Col, Form, Row, Tab, Tabs, Toast, ToastContainer } from 'react-bootstrap';
import { useMainLayoutContext } from 'providers/MainLayoutProvider';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProfileCard from './components/ProfileCard';
import avatar from 'assets/img/team/150x150/avatar.webp';
import { HoganUserData, InterestsState, PreferencesState, ProfileData, UserData } from './components/DataDefinitions';
import apiClient, { handleAPIError } from 'helpers/apiService';

import { useAuth } from 'providers/AuthProvider'
import PersonalInfo from './components/PersonalInfo';
import CompanyInfo from './components/CompanyInfo';
import Preferences from './components/Preferences';
import SideBar from './components/SideBar';
import UserLoginInfo from './components/UserInfo';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import HoganLoginInfo from './components/HoganLoginInfo';


const breadcrumbItems: PageBreadcrumbItem[] = [
    {
        label: 'Dashboard',
        url: '/dashboard/home'
    },
    {
        label: 'Users (Admin)',
        url: '/admin/users'
    },
    {
        label: 'Update User (Admin)',
        active: true
    }
];

const AdminUserProfileUpdate = () => {

    const { id } = useParams();

    const { setFooterClass } = useMainLayoutContext();
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const auth = useAuth();
    const navigate = useNavigate();
    // Form validation
    const [validated, setValidated] = useState(false);
    const [errors, setErrors] = useState<{
        email?: string, password_mismatch?: string
    }>({});

    const [profileErrors, setProfileErrors] = useState<{
        email?: string, first_name?: string; last_name?: string;
    }>({});

    const [hoganErrors, setHoganErrors] = useState<{
        login?: string, password?: string
    }>({});

    // State to control Toast messages
    const [show, setShow] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const [profileImage, setProfileImage] = useState<any>(null);

    const [user, setUser] = useState<UserData>({
        user_id: 0, login: '', disabled: false, first_name: '', last_name: '',
        customer: { customer_id: 0, customer_name: '', }, role: '', inserted_at: '',
        password: 'secret', confirmPassword: 'secret'
    });

    const [profile, setProfile] = useState<ProfileData>({
        profile_id: 0,
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        picture: null,  // base64,
        location: '',

        company_department: '',
        linkedin_url: '',

        is_coach: 'off',
        is_mentor: 'off',

        about_me: '',
        inserted_at: '',
        contacts_count: 0,
        coach_types: '',
        mentor_types: '',

        question1: '', question2: '', question3: '',
        preferences: {
            careerCoaching: false,
            internalMobility: false,
            leadershipCoaching: false,
            mentoringLeadership: false,
            mentoringNewPosition: false,
            mentoringSales: false,
            shadowMentoring: false,
            teamCoaching: false,
        },
        interests: {
            art: false,
            cinemaTheatre: false,
            creativeWriting: false,
            cooking: false,
            reading: false,
            sport: false,
            technology: false,
            traveling: false,
            yogaMeditation: false,
        },
        services: '',
        coaching_method: ''
    });

    const [hoganUser, setHoganUser] = useState<HoganUserData>({
        login: '', password: 'secret', user_id: id, hogan_available: false
    });


    const fetchUser = async (id: any) => {
        setLoading(true);
        try {
            const response = await apiClient.get(
                `/v1/admin/users/${id}`,
                {
                    headers: {
                        'accept': 'application/json',

                    },
                    withCredentials: true,
                }
            );

            setUser({
                ...response.data,
                password: 'secret',
                confirmPassword: 'secret'
            });

            fetchProfile(id);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchProfile = async (id: any) => {
        setLoading(true);
        try {
            const response = await apiClient.get(
                `/v1/admin/users/${id}/profile`,
                {
                    headers: {
                        'accept': 'application/json',

                    },
                    withCredentials: true,
                }
            );
            /*
            setUser({
                ...user,
                first_name: response.data.first_name,
                last_name: response.data.last_name,
            });*/
            setProfile(response.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUser(id);

        setFooterClass('d-none d-lg-block');
        return () => {
            setFooterClass('');
        };

    }, []);


    const handlePreferencesChange = (preferences: PreferencesState) => {
        setProfile({
            ...profile,
            preferences: preferences,
        });
    };

    const handleInterestsChange = (interests: InterestsState) => {
        setProfile({
            ...profile,
            interests: interests,
        });
    };

    const handleProfileChange = (name: string, value: any) => {
        setProfile({
            ...profile, [name]: value
        });
    };

    const handleUserChange = (name: string, value: any) => {
        setUser({
            ...user, [name]: value
        });
    };

    const handleHoganUserChange = (name: string, value: any) => {
        setHoganUser({
            ...hoganUser, [name]: value
        });
    };

    const handleProfileImageChange = async (image: File | null) => {
        if (image) {
            setProfileImage(image);
            const form = new FormData();
            form.append('file', image);

            try {
                const response = await apiClient.put(
                    `/v1/profile/${profile.profile_id}/image`, form,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',

                        }
                    }
                );
                setToastMessage(response.data['message'])
                setShow(true);
            } catch (error) {
                console.error('Request failed:', error);
            }

        }
    };


    const handleSubmit = async (event: any) => {
        const form = event.currentTarget;
        if (validateUserForm() === false) {
            event.preventDefault();
            event.stopPropagation();
            return
        }

        setValidated(true);

        let formData = new FormData();
        formData.append('userForm', JSON.stringify(user));
        try {
            const response = await apiClient.put(
                `/v1/admin/user`, formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                }
            );
            setToastMessage(response.data['message']);
            setShow(true);

        } catch (error) {
            console.error('Request failed:', error);
            const message = handleAPIError(error);
            if (message) {
                setToastMessage(message);
                setShow(true);
            }
        }

        // profile update
        formData = new FormData();
        formData.append('profileForm', JSON.stringify(profile));
        try {
            const response = await apiClient.put(
                `/v1/profile`, formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );
            setToastMessage(response.data['message'])
            setShow(true);
            navigate("/admin/users")

        } catch (error) {
            console.error('Request failed:', error);
            const message = handleAPIError(error);
            if (message) {
                setToastMessage(message);
                setShow(true);
            }
        }

    };

    const validateUserForm = (): boolean => {
        const newErrors: { email?: string; first_name?: string; last_name?: string; password_mismatch?: string } = {};
        let isValid = true;
        if (!user.login) {
            newErrors.email = 'Email is required';
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(user.login)) {
            newErrors.email = 'Email is not valid';
            isValid = false;
        }

        // Validate first/last name
        /*
        if (!user.first_name || user.first_name.trim() === '') {
            newErrors.first_name = 'First name is required';
            isValid = false;
        }
        if (!user.last_name || user.last_name.trim() === '') {
            newErrors.last_name = 'Last name is required';
            isValid = false;
        }
        */
        if (user.password != user.confirmPassword || user.password.trim() == '') {
            newErrors.password_mismatch = 'Password & confirm password must match and being not empty';
            isValid = false;
        }

        // Set any errors in the state
        setErrors(newErrors);
        return isValid;
    };

    const validateHoganUserForm = (): boolean => {
        const newErrors: { login?: string; password?: string } = {};

        let isValid = true;
        if (!user.login) {
            newErrors.login = 'Login is required';
            isValid = false;
        }

        if (!user.password || user.password.trim() == '') {
            newErrors.password = 'Password cannot be empty';
            isValid = false;
        }
        // Set any errors in the state
        setHoganErrors(newErrors);
        return isValid;
    };

    const handleHoganSubmit = async (event: any) => {
        event.preventDefault();
        event.stopPropagation();

        if (hoganUser.user_id == null)
            return

        if (validateHoganUserForm() === false) {
            return
        }

        setValidated(true);

        const formData = new FormData();
        formData.append('hoganUserForm', JSON.stringify(hoganUser));
        try {
            const response = await apiClient.put(
                `/v1/admin/user/hogan`, formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );
            setToastMessage(response.data['message']);
            setShow(true);

        } catch (error) {
            console.error('Request failed:', error);
            const message = handleAPIError(error);
            if (message) {
                setToastMessage(message);
                setShow(true);
            }
        }



    };

    const handleChangeTab = async (eventKey: any) => {
        //console.log("handleChangeTab:" + eventKey);
        if (eventKey === 'hogan') {
            try {
                const response = await apiClient.get(
                    `/v1/admin/user/hogan/${id}`,
                    {
                        headers: {
                            'accept': 'application/json',

                        },
                        withCredentials: true,
                    }
                );
                if (response.status === 200) {
                    setHoganUser(response.data);
                }

            } catch (error) {
                console.error('Request failed:', error);
                const message = handleAPIError(error);
                if (message) {
                    setToastMessage(message);
                    setShow(true);
                    return
                }
            }
        }
    };



    if (loading) {
        return <div>Loading...</div>;
    }


    return (
        <>
            <PageBreadcrumb items={breadcrumbItems} />
            <div>
                <ToastContainer className="p-3" position='top-center'>
                    <Toast onClose={() => { setShow(false); }} show={show} delay={5000} autohide>
                        <Toast.Body>{toastMessage}</Toast.Body>
                    </Toast>
                </ToastContainer>
            </div>
            <Tabs
                defaultActiveKey="-"
                className="mb-3 nav-underline"
                onSelect={handleChangeTab}
            >
                <Tab eventKey="-" title="User">
                    <Row className="g-6 mb-1">
                        <Col xs={4} md={4}>
                            <ProfileCard avatar={profileImage ? profileImage : avatar} className="border border-light h-100" showAbout={true} profile={profile} onProfileChange={handleProfileChange}
                                onChangeProfileImage={handleProfileImageChange}
                                onChangeAboutMe={() => { }}
                            />
                        </Col>
                        <Col xs={8} md={8}>
                            <UserLoginInfo user={user} isAdd={false} onUserChange={handleUserChange} errors={errors} />
                        </Col>
                        <div className="text-end mb-6">
                            <Button variant="phoenix-secondary" className="me-2" onClick={() => fetchUser(id)}>
                                {t("UserProfile.cancel_changes")}
                            </Button>
                            <Button variant="phoenix-primary" onClick={handleSubmit} >{t("UserProfile.save_information")}</Button>
                        </div>

                    </Row>
                </Tab>
                <Tab eventKey="personal-info" title={t("UserProfile.tab1")}>

                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
                        <div className="mb-1">
                            <Row className="g-6">
                                <PersonalInfo profile={profile} onProfileChange={handleProfileChange} errors={profileErrors} />
                            </Row>

                            <Row className="g-6">
                                <CompanyInfo profile={profile} onProfileChange={handleProfileChange} />
                            </Row>

                            <Row className="g-6">
                                <SideBar profile={profile} onProfileChange={handleProfileChange} />
                            </Row>
                            <Row>
                                <Col xs={12}>
                                    {<Preferences preferences={profile.preferences} interests={profile.interests} onPreferencesChange={handlePreferencesChange}
                                        onInterestsChange={handleInterestsChange}
                                    />}
                                </Col>
                            </Row>

                            <Row>
                                <div className="text-end mb-6">
                                    <Button variant="phoenix-secondary" className="me-2" onClick={() => fetchProfile(id)}>
                                        {t("UserProfile.cancel_changes")}
                                    </Button>
                                    <Button variant="phoenix-primary" type="submit">{t("UserProfile.save_information")}</Button>
                                </div>
                            </Row>
                        </div>
                    </Form>
                </Tab>

                <Tab eventKey="hogan" title={t("UserProfile.tab_hogan")}>
                    <Form noValidate validated={validated} onSubmit={handleHoganSubmit}>
                        <HoganLoginInfo hoganUser={hoganUser} isAdd={false} onUserChange={handleHoganUserChange} errors={hoganErrors} />

                        <Row>
                            <div className="text-end mt-3">
                                <Button variant="phoenix-primary" type="submit">{t("UserProfile.save_hogan_information")}</Button>
                            </div>
                        </Row>

                    </Form>
                </Tab>
            </Tabs>


        </>
    );
};

export default AdminUserProfileUpdate;
